import { useRouter } from "next/router";
import { setFileUnit, setMaxFileSize, setOptimizationLevel, setSaveAs } from "store/features/bulkResize/actions";
import { bulkResizeSelector } from "store/features/bulkResize/selectors/bulkResizeSelector";
import { BulkResizeImage, BulkResizeState, FileUnit, OptimizationLevel } from "store/features/bulkResize/types";
import { getImportedImages } from "store/features/bulkResize/utils/getImportedImages";
import { getUnImportedImages } from "store/features/bulkResize/utils/getUnImportedImages";
import { useAppDispatch, useAppSelector } from "store/store";
import { ImageExtension } from "store/types/imageExtension";
import { validateResizeDimensions } from "store/features/bulkResize/utils/validateResizeDimensions";
import { useState } from "react";
import { ga } from "client/utils/adsense/ga";
import { gtag } from "client/utils/adsense/gtag";
import type { Job, Task } from "@freeconvert/freeconvert-node/dist/types";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createResizeJob, ResizeJobPayload } from "store/features/resizeEditor/extraReducers/createResizeJob";
import { convertToPixels } from "client/utils/pixels/convertToPixels";
import translatedRoutedSlugs from "translatedRouteSlugs";
import { useToast } from "components/common/Toast/useToast";
import { truncateImageTitle } from "client/utils/truncateImageTitle";
import { translateFunc } from "client/utils/translateFunc";
import { getFileExtFromTask } from "client/utils/getFileExtFromTask";
import { createExportTask } from "@/utils/createExportTask";
import { useDailyQuota } from "@/modules/DailyQuota/hooks/useDailyQuota";
import { getImageExtensionFromTitle } from "@/utils/getImageExtension";
import { getCropBox } from "@/store/features/bulkResize/utils/getCropBox";
import { getFlipRotateData } from "@/store/features/bulkResize/utils/getFlipRotateData";

export const useExportSettings = () => {
    const bulkResizerState = useAppSelector(bulkResizeSelector);
    const dispatch = useAppDispatch();
    const importedImages = getImportedImages(bulkResizerState.images);
    const unImportedImages = getUnImportedImages(bulkResizerState.images);
    const router = useRouter();
    const [isProcessing, setIsProcessing] = useState(false);
    const { toast } = useToast();
    const { consumeQuota, getRemainingQuota, showQuotaLimitExceededToast } = useDailyQuota();

    const validDimensions = validateResizeDimensions(
        importedImages,
        bulkResizerState.resizeSettings.dimensionType,
        bulkResizerState.resizeSettings.unit,
        bulkResizerState.resizeSettings.byPercentage.percentage,
        bulkResizerState.exportSettings.saveAs,
        bulkResizerState.resizeSettings.unit === "pixels" ? undefined : +bulkResizerState.resizeSettings.dpi,
    );
    const isProceedButtonActive =
        unImportedImages.length === 0 && importedImages.length > 0 && validDimensions && !isProcessing;

    const handleOptimizationLevelChange = (optimizationLevel: OptimizationLevel) => {
        dispatch(setOptimizationLevel(optimizationLevel));
    };

    const handleMaxFileSizeChange = (value: number) => {
        dispatch(setMaxFileSize(value));
    };

    const handleFileUnitChange = (fileUnit: FileUnit) => dispatch(setFileUnit(fileUnit));
    const handleSaveAsChange = (extension: ImageExtension | "original") => {
        if (extension === ".png" && bulkResizerState.exportSettings.optimizationLevel === "custom") {
            handleOptimizationLevelChange("best");
        }

        dispatch(setSaveAs(extension));
    };

    const handleProceedAnalytics = () => {
        ga("send", {
            hitType: "pageview",
            page: "/vp/bulk_resizer/export_started/",
            title: "Bulk Resizer Export Started",
        });

        ///event
        gtag("event", "Export Started", {
            event_category: "Bulk Resizer",
            event_label: "Editor Page",
        });

        gtag(
            "event",
            `Optimization Level ${
                bulkResizerState.exportSettings.optimizationLevel !== "custom"
                    ? bulkResizerState.exportSettings.optimizationLevel
                    : bulkResizerState.exportSettings.maxFileSize === 0
                      ? "empty"
                      : `custom`
            }`,
            {
                event_category: "Bulk Resizer",
                event_label: "Editor Page",
            },
        );
    };

    const handleProceed = async () => {
        if (!isProceedButtonActive) return;
        setIsProcessing(true);
        handleProceedAnalytics();
        // to refresh the daily quota data
        await getRemainingQuota();
        const result = await createJobs();
        if (!result) {
            setIsProcessing(false);
            return;
        }
        const importedImages = getImportedImages(bulkResizerState.images);
        const hasExceededQuota = importedImages.length > (result.dependsOn?.length || 0);

        const urlPrefix = () => {
            if (router.asPath.includes("resize-jpg")) return "resize-jpg";
            if (router.asPath.includes("resize-png")) return "resize-png";
            if (router.asPath.includes("resize-webp")) return "resize-webp";
            if (router.asPath.includes("bulk-resize")) return "bulk-resize";
            return "resize";
        };

        const locale = router.locale || "en";
        let url =
            `${locale === "en" ? "" : `/${locale}`}/${(translatedRoutedSlugs[`/${urlPrefix()}/download/:taskId`] as any)?.[locale] ?? `${urlPrefix()}/download/:taskId`}`.replace(
                ":taskId",
                result.id,
            );
        if (hasExceededQuota) url += "?quotaExceeded=true";
        router.push(url);
    };

    const conversionFailedText = translateFunc("conversion-failed-toast-message");
    const exportFailedText = translateFunc("export-failed-toast-message");
    const createJobs = async () => {
        try {
            const importedImages = getImportedImages(bulkResizerState.images);
            const data = await Promise.allSettled(
                importedImages.map((image) => {
                    if (!image.importTask) return;
                    return dispatch(
                        createResizeJob(getCreateResizeJobPayload(image, image.importTask, bulkResizerState)),
                    );
                }),
            );

            const jobs: (Job & {
                payload: any;
            })[] = [];

            data.forEach((result, index) => {
                if (result.status === "rejected") {
                    toast({
                        position: "top-right",
                        status: "error",
                        text: `${truncateImageTitle(importedImages[index].title, 10)} ${conversionFailedText}.`,
                        time: 7000,
                    });
                }
                if (!result || result.status !== "fulfilled" || !result.value?.payload) return;
                const job = result.value.payload as PayloadAction<{ result: Job }>["payload"];
                const payload = result.value.payload;
                job.result && jobs.push({ ...job.result, payload });
            });

            if (!jobs || !jobs.length) {
                showQuotaLimitExceededToast();
                return;
            }

            const taskIds = new Set<string>();
            jobs.flatMap((job) => job.tasks || []).forEach((task) => {
                if (task.name === "exportTask") taskIds.add(task.id);
            });
            consumeQuota(taskIds.size);

            const fileName = taskIds.size > 1 ? "resized-images.zip" : undefined;
            const { error, result } = await createExportTask([...taskIds], fileName);
            if (error || !result) {
                const { error, result } = await createExportTask([...taskIds], fileName);
                if (error || !result) {
                    setIsProcessing(false);
                    toast({
                        position: "top-right",
                        status: "error",
                        text: exportFailedText,
                        time: 7000,
                    });
                    return;
                }
                return result;
            }
            return result;
        } catch (e) {}
    };

    return {
        fileUnit: bulkResizerState.exportSettings.fileUnit,
        maxFileSize: bulkResizerState.exportSettings.maxFileSize,
        optimizationLevel: bulkResizerState.exportSettings.optimizationLevel,
        saveAs: bulkResizerState.exportSettings.saveAs,
        handleOptimizationLevelChange,
        handleMaxFileSizeChange,
        handleFileUnitChange,
        handleSaveAsChange,
        handleProceed,
        unImportedImages,
        validDimensions,
        unit: bulkResizerState.resizeSettings.unit,
        isProceedButtonActive,
        isProcessing,
        importedImages,
    };
};

const getCreateResizeJobPayload = (
    image: BulkResizeImage,
    importTask: Task,
    bulkResizerState: BulkResizeState,
): ResizeJobPayload => {
    const cropData = {
        inputFormat: getFileExtFromTask(importTask),
        outputFormat: getFileExtFromTask(importTask) || getImageExtensionFromTitle(image.title) || image.format,
        inputTask: importTask,
        options: getCropBox(image, image.operations),
    };

    const { rotation, flipH, flipV } = getFlipRotateData(image.operations);

    const optimization_level =
        bulkResizerState.exportSettings.optimizationLevel !== "custom"
            ? bulkResizerState.exportSettings.optimizationLevel
            : bulkResizerState.exportSettings.maxFileSize === 0
              ? "best"
              : undefined;

    let width, height;

    if (bulkResizerState.resizeSettings.dimensionType === "percentage") {
        const percentage = bulkResizerState.resizeSettings.byPercentage.percentage / 100;
        width = Math.round(image.original.width * percentage);
        height = Math.round(image.original.height * percentage);
    } else {
        const dpi = bulkResizerState.resizeSettings.unit !== "pixels" ? +bulkResizerState.resizeSettings.dpi : 1;

        width = Math.round(convertToPixels(image.preview.width, bulkResizerState.resizeSettings.unit, dpi));

        height = Math.round(convertToPixels(image.preview.height, bulkResizerState.resizeSettings.unit, dpi));
    }

    return {
        crop: image.crop.cropBox ? cropData : null,
        importTask,
        resize: {
            dimensions: {
                width,
                height,
            },
            inputFormat: getFileExtFromTask(importTask),
            outputFormat:
                bulkResizerState.exportSettings.saveAs === "original"
                    ? image.format.replace(".", "")
                    : bulkResizerState.exportSettings.saveAs.replace(".", ""),
            options: {
                dpi:
                    bulkResizerState.resizeSettings.dimensionType === "pixels" &&
                    bulkResizerState.resizeSettings.unit !== "pixels"
                        ? +bulkResizerState.resizeSettings.dpi
                        : undefined,
                background:
                    bulkResizerState.resizeSettings.dimensionType === "percentage"
                        ? undefined
                        : bulkResizerState.resizeSettings.isLocked
                          ? undefined
                          : !bulkResizerState.resizeSettings.fillColor.shouldFillColor
                            ? undefined
                            : bulkResizerState.resizeSettings.fillColor.type === "color"
                              ? bulkResizerState.resizeSettings.fillColor.color
                              : "none",
                fillBackground:
                    bulkResizerState.resizeSettings.dimensionType === "percentage"
                        ? false
                        : bulkResizerState.resizeSettings.isLocked
                          ? false
                          : bulkResizerState.resizeSettings.fillColor.shouldFillColor,
                maxFileSize:
                    bulkResizerState.exportSettings.optimizationLevel === "custom" &&
                    bulkResizerState.exportSettings.maxFileSize > 0
                        ? bulkResizerState.exportSettings.fileUnit === "KB"
                            ? +bulkResizerState.exportSettings.maxFileSize
                            : +bulkResizerState.exportSettings.maxFileSize * 1000
                        : undefined,
                rotation: rotation,
                horizontalFlip: flipH,
                verticalFlip: flipV,
            },
            percentage: null,
            resize_as: "dimensions",
            optimization_level,
        },
        tag: "bulk-resize-editor-conversion",
        extra: {
            original_width: image.original.width,
            original_height: image.original.height,
            file_name: image.title,
            new_width: width,
            new_height: height,
        },
    };
};
